import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Title from "@/components/molecules/Title";
import { Collapse, FormControl, FormControlLabel, Grid, Link, Paper, Radio, RadioGroup } from "@mui/material";
import { Messages } from "@/util/Messages";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@/components/atoms/TextField";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { CarNoopConfirmDialog } from "@/components/carUnderuse/CarNoopConfirmDialog";
import { Typography } from "@/components/atoms/Typography";
import { formatDateStr, getShortDatetime } from "@/util/datetime";
import { AxiosError } from "axios";
import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import toast from "@/components/hoc/SnackbarAlert";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";
import { ACCIDENT_FORM_URL, CarNoopEventsConstants, CarNoopEventsEditState, CarNoopEventsEditValidationError, getEditWarnMessage, resolveEditRequest, RESTART_PLAN, RestartDateType, NOOP_REASONS, INITIAL_EDIT_STATE, CRITICAL_ACCIDENT_URL, ACCIDENT_TYPE, AfterSubmitDialogInfo, resolveAfterSubmitDialogInfo, ACCIDENT_OCCURRENCE_OPTIONS, ACCIDENT_REPORT_OPTIONS, NO_ACCIDENT_OPTIONS, ACCIDENT_OCCURRENCE_KEYS, ACCIDENT_FEATURE_KEYS, RESTART_DATE_TYPE_KEYS, getAccidentSelectionMessage } from "./CarNoopEventsConstants";
import { EvCarNoopEvents, NoopReasonCode } from "@/models/carUnderuseEvents";
import { getCarNoopEventAPI, editCarNoopEventsAPI } from "@/util/api/carUnderuseEvents";
import { AccidentReport } from "@/models/accidentReport";
import { getAccidentReportsForNoopAPI } from "@/util/api/accidentReports";

export const CarNoopEventsEditPage = () => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const [carNoopEvent, setCarNoopEvent] = React.useState<EvCarNoopEvents>();
  const [editState, setEditState] = React.useState<CarNoopEventsEditState>(INITIAL_EDIT_STATE);
  const [restartOptions, setRestartOptions] = React.useState<{ key: string, value: string }[]>(RESTART_PLAN);
  const [showRestartUnknownOnly, setShowRestartUnknownOnly] = React.useState(false);
  const [accidentReports, setAccidentReports] = React.useState<AccidentReport[]>([]);
  const [validationErrors, setValidationErrors] = React.useState<CarNoopEventsEditValidationError>({});

  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showConfirmWarning, setShowConfirmWarning] = React.useState(false);
  const [afterSubmitDialogInfo, setAfterSubmitDialogInfo] = React.useState<AfterSubmitDialogInfo>();

  const location = useLocation();
  const navigate = useNavigate();

  // 一覧画面遷移
  const goToList = React.useCallback(
    () =>
      navigate(`/car_noop_events`, {
        state: location.state,
      }),
    [location.state, navigate]
  );

  // 事故発生有無の変更ハンドラー
  const handleChangeAccidentOccurrence = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      setValidationErrors((prev) => ({ ...prev, accidentOccurrence: false, selectedAccidentFeature: false, restartPlanDateKey: false }));

      setEditState((prev) => ({
        ...prev,
        accidentOccurrence: value,
        selectedAccidentFeature: undefined,
        underuseReason: undefined,
        accidentReportId: undefined,
        restartPlanDateKey: undefined,
      }));
    },
    []
  );

  // 事故報告選択の変更ハンドラー
  const handleChangeAccidentSelection = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      setValidationErrors((prev) => ({ ...prev, selectedAccidentFeature: false }));

      setEditState((prev) => {
        let underuseReason: NoopReasonCode | undefined;
        let accidentReportId: number | undefined;

        switch (value) {
          case ACCIDENT_FEATURE_KEYS.NOT_REPORTED:
            // 事故報告フォーム未提出
            underuseReason = NOOP_REASONS.UNREPORTED_ACCIDENT;
            accidentReportId = undefined;
            break;
          case ACCIDENT_FEATURE_KEYS.NOT_IN_LIST:
            // 事故報告フォームを提出したが上記にない
            underuseReason = NOOP_REASONS.REPORTED_ACCIDENT;
            accidentReportId = undefined;
            break;
          default:
            // 事故IDが選択された
            underuseReason = NOOP_REASONS.REPORTED_ACCIDENT;
            accidentReportId = Number(value);
            break;
        }

        return {
          ...prev,
          selectedAccidentFeature: value,
          underuseReason,
          accidentReportId,
        };
      });

      // 事故報告済みの場合は「2週間以上/不明」を自動選択
      if (value !== ACCIDENT_FEATURE_KEYS.NOT_REPORTED) {
        setShowRestartUnknownOnly(true);
        setEditState((prev) => ({
          ...prev,
          restartPlanDateKey: RESTART_DATE_TYPE_KEYS.UNKNOWN,
        }));
        setRestartOptions(RESTART_PLAN.filter(option => option.key === RESTART_DATE_TYPE_KEYS.UNKNOWN));
      } else {
        // 未提出の場合は選択をクリアして警告を表示
        setShowRestartUnknownOnly(false);
        setRestartOptions(RESTART_PLAN);
        setEditState((prev) => ({
          ...prev,
          restartPlanDateKey: undefined,
        }));
      }

      setShowConfirmWarning(false);
    },
    []
  );

  // 非事故選択肢の変更ハンドラー
  const handleChangeNoAccidentSelection = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      setValidationErrors((prev) => ({ ...prev, selectedAccidentFeature: false }));

      let underuseReason: NoopReasonCode | undefined;

      switch (value) {
        case ACCIDENT_FEATURE_KEYS.NON_ACCIDENT_FAILURE:
          underuseReason = NOOP_REASONS.NON_ACCIDENT_FAILURE;
          break;
        case ACCIDENT_FEATURE_KEYS.OTHER:
          underuseReason = NOOP_REASONS.OTHER;
          break;
      }

      setEditState((prev) => ({
        ...prev,
        selectedAccidentFeature: value,
        underuseReason,
        accidentReportId: undefined,
        restartPlanDateKey: undefined,
      }));

      setShowConfirmWarning(true);
      setShowRestartUnknownOnly(false);
      setRestartOptions(RESTART_PLAN);
    },
    []
  );

  const handleChangeRestartDateType = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as RestartDateType;

      setValidationErrors((prev) => ({ ...prev, restartPlanDateKey: false, accidentOccurrence: false, selectedAccidentFeature: false }));

      switch (value) {
        case RESTART_DATE_TYPE_KEYS.NOT_NOOP:
          setEditState((prev) => ({
            ...prev,
            accidentOccurrence: undefined,
            selectedAccidentFeature: undefined,
            underuseReason: undefined,
            accidentReportId: undefined,
            restartPlanDateKey: value,
            falseDetection: true,
          }));
          setShowConfirmWarning(false);
          break;
        default:
          setEditState((prev) => ({
            ...prev,
            restartPlanDateKey: value,
            falseDetection: undefined,
          }));
          break;
      }
    },
    []
  );

  const handleChangeInjuredExist = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditState((prev) => ({
        ...prev,
        accidentTypeKey: event.target.value,
      }));
    },
    []
  );

  const handleAfterSubmitDialogClose = React.useCallback(
    (withNavigate: boolean) => {
      setAfterSubmitDialogInfo(undefined);
      if (withNavigate) {
        window.open(ACCIDENT_FORM_URL, '_blank', 'noopener, noreferrer');
      }
      goToList();
    },
    [goToList]
  );

  const [loading, setLoading] = React.useState(false);
  const upSert = React.useCallback(async () => {
    if (loading) {
      return;
    }
    setLoading(true);

    const request = resolveEditRequest(editState);

    editCarNoopEventsAPI(carNoopEvent!.eventId, request)
      .then(() => {
        const info = resolveAfterSubmitDialogInfo(editState.underuseReason);
        setAfterSubmitDialogInfo(info);

        if (!info) {
          setLoading(false);
          goToList();
        }
      })
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([404, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Replace}${Messages.ErrorOccurred}: ${(e as AxiosError<ErrorResponse>)?.response?.data?.message ?? ""
            }`
          );
        }

        console.log("===Error occurred while update EvCarUnderuseEvents===", e);
      })
      .finally(() => setLoading(false));
  }, [editState, carNoopEvent, loading, goToList]);

  // Handle submit, check validate and fetch API update
  const onSubmit = React.useCallback(() => {
    // Check validate with each field
    const validationErrorsTemp: CarNoopEventsEditValidationError = {};

    // 稼働再開見込みが選択されていない場合
    if (!editState.restartPlanDateKey) {
      validationErrorsTemp.restartPlanDateKey = true;
    }

    // 「該当期間稼働しており、未稼働ではない」が選択されている場合は、事故関連のバリデーションをスキップ
    if (editState.restartPlanDateKey !== RESTART_DATE_TYPE_KEYS.NOT_NOOP) {
      // 事故発生有無が選択されていない、または選択肢が選ばれていない場合
      if (!editState.accidentOccurrence) {
        validationErrorsTemp.accidentOccurrence = true;
      }

      if (editState.accidentOccurrence && !editState.selectedAccidentFeature) {
        validationErrorsTemp.selectedAccidentFeature = true;
      }
    }

    if (validationErrorsTemp.accidentOccurrence || validationErrorsTemp.selectedAccidentFeature || validationErrorsTemp.restartPlanDateKey) {
      setValidationErrors(validationErrorsTemp);
      return;
    }

    setShowConfirm(true);
  }, [editState]);

  const getCarUnderuseEvent = React.useCallback(
    () => {
      getCarNoopEventAPI(id)
        .then((res) => {
          const event = res.data;
          setCarNoopEvent(event);
        })
        .catch((error) => {
          console.error("Error fetching car underuse event:", error);
          goToList();
        });
    }, [id, goToList]
  );

  // Load CarUnderuseEvent
  React.useEffect(() => {
    getCarUnderuseEvent();
  }, [getCarUnderuseEvent]);

  // Load Accident Reports
  React.useEffect(() => {
    if (carNoopEvent?.evCarId && carNoopEvent?.eventId) {
      getAccidentReportsForNoopAPI(carNoopEvent.evCarId, carNoopEvent.eventId)
        .then((response) => {
          setAccidentReports(response.data);
        })
        .catch((error) => {
          console.error("Error fetching accident reports:", error);
          setAccidentReports([]);
        });
    }
  }, [carNoopEvent?.evCarId, carNoopEvent?.eventId]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{CarNoopEventsConstants.Edit}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                }}
                className="gx-info-table"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarNoopEventsConstants.CompanyOffice}
                        value={carNoopEvent?.companyName + " / " + carNoopEvent?.officeName || ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarNoopEventsConstants.RadioNumber}
                        value={carNoopEvent?.vehicleCode || "-"}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarNoopEventsConstants.PlateNumber}
                        value={carNoopEvent?.plateNumberText || "-"}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarNoopEventsConstants.NoopStartDate}
                        value={`${formatDateStr(carNoopEvent?.noopStartDate)}`}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={CarNoopEventsConstants.EventAt}
                        value={getShortDatetime(carNoopEvent?.eventAt)}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: "none" }}>
                      <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                        {CarNoopEventsConstants.AccidentOccurrence}
                      </Typography>
                      <FormControl component="fieldset" error={validationErrors.accidentOccurrence}>
                        <RadioGroup
                          value={editState.accidentOccurrence ?? ""}
                          onChange={handleChangeAccidentOccurrence}
                        >
                          {ACCIDENT_OCCURRENCE_OPTIONS.map((option) => (
                            <React.Fragment key={option.key}>
                              <FormControlLabel
                                value={option.key}
                                control={<Radio />}
                                label={option.value}
                              />
                              <Typography variant="body2" sx={{ ml: 4, mb: 1, color: "text.secondary" }}>
                                {option.note}
                              </Typography>

                              {/* 事故が選択された場合 - 事故ラジオボタンの直下に表示 */}
                              {option.key === ACCIDENT_OCCURRENCE_KEYS.ACCIDENT && (
                                <Collapse in={editState.accidentOccurrence === ACCIDENT_OCCURRENCE_KEYS.ACCIDENT} timeout="auto" unmountOnExit>
                                  <Grid container direction="column" sx={{ mt: 1, mb: 2, ml: 4 }}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                      {getAccidentSelectionMessage(accidentReports)}
                                    </Typography>

                                    <FormControl component="fieldset" error={validationErrors.selectedAccidentFeature}>
                                      <RadioGroup
                                        value={editState.selectedAccidentFeature ?? ""}
                                        onChange={handleChangeAccidentSelection}
                                      >
                                        {/* 事故報告一覧 */}
                                        {accidentReports.length === 0 ? (
                                          <FormControlLabel
                                            value={ACCIDENT_FEATURE_KEYS.NOT_IN_LIST}
                                            control={<Radio />}
                                            label={ACCIDENT_REPORT_OPTIONS.REPORTED}
                                          />
                                        ) : (
                                          accidentReports.map((report) => (
                                            <FormControlLabel
                                              key={report.id}
                                              value={String(report.id)}
                                              control={<Radio />}
                                              label={`発生日時 ${getShortDatetime(report.happenedAt)} / 報告日時 ${getShortDatetime(report.reportedAt)}`}
                                            />
                                          ))
                                        )}

                                        {/* 追加の選択肢 */}
                                        {accidentReports.length > 0 && (
                                          <FormControlLabel
                                            value={ACCIDENT_FEATURE_KEYS.NOT_IN_LIST}
                                            control={<Radio />}
                                            label={ACCIDENT_REPORT_OPTIONS.NOT_IN_LIST}
                                          />
                                        )}
                                        <FormControlLabel
                                          value={ACCIDENT_FEATURE_KEYS.NOT_REPORTED}
                                          control={<Radio />}
                                          label={ACCIDENT_REPORT_OPTIONS.NOT_REPORTED}
                                        />
                                      </RadioGroup>
                                      {validationErrors.selectedAccidentFeature && (
                                        <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
                                          選択肢を選んでください
                                        </Typography>
                                      )}
                                    </FormControl>

                                    {/* 未提出の場合の区分選択 */}
                                    <Collapse in={editState.selectedAccidentFeature === ACCIDENT_FEATURE_KEYS.NOT_REPORTED} timeout="auto" unmountOnExit>
                                      <Grid container direction="column" sx={{ mt: 1, ml: 4 }}>
                                        <Typography variant="body1">
                                          {CarNoopEventsConstants.AccidentTypeSelection}
                                        </Typography>
                                        <FormControl component="fieldset">
                                          <RadioGroup
                                            value={editState.accidentTypeKey}
                                            onChange={handleChangeInjuredExist}
                                            row
                                            sx={{ mt: 1 }}
                                          >
                                            {ACCIDENT_TYPE.map((option) => (
                                              <FormControlLabel
                                                key={option.key}
                                                value={option.key}
                                                control={<Radio />}
                                                label={option.value}
                                              />
                                            ))}
                                          </RadioGroup>
                                        </FormControl>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          区分1：重大事故(<Link href={CRITICAL_ACCIDENT_URL} target="_blank" rel="noopener noreferrer">国交省への報告対象事案</Link>)・人身事故・SNS炎上事案・GI基金に関する情報漏洩<br />
                                          区分2：区分1以外の事故<br />
                                          ※怪我人がいる場合、事故処理内容に関わらず区分1でご報告ください
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 2 }}>
                                          ※事故報告フォーム未提出の場合、NEDO様への<Link href={ACCIDENT_FORM_URL} target="_blank" rel="noopener noreferrer">事故報告フォーム</Link>のご提出をお願いいたします。<br />
                                          ※かねてよりご案内の通り、EVや充電設備での事故発生時は、NEDO様より本プロジェクトの助成要件として、24時間以内のご報告を求められております。
                                        </Typography>
                                      </Grid>
                                    </Collapse>
                                  </Grid>
                                </Collapse>
                              )}
                            </React.Fragment>
                          ))}
                        </RadioGroup>
                        {validationErrors.accidentOccurrence && (
                          <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
                            事故発生有無を選択してください
                          </Typography>
                        )}
                      </FormControl>

                      {/* 事故報告漏れはないが選択された場合 */}
                      <Collapse in={editState.accidentOccurrence === ACCIDENT_OCCURRENCE_KEYS.NO_ACCIDENT} timeout="auto" unmountOnExit>
                        <Grid container direction="column" sx={{ mt: 1, ml: 4 }}>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            {CarNoopEventsConstants.UnderuseReasonSelection}
                          </Typography>
                          <FormControl component="fieldset" error={validationErrors.selectedAccidentFeature}>
                            <RadioGroup
                              value={editState.selectedAccidentFeature ?? ""}
                              onChange={handleChangeNoAccidentSelection}
                            >
                              {NO_ACCIDENT_OPTIONS.map((option) => (
                                <FormControlLabel
                                  key={option.key}
                                  value={option.key}
                                  control={<Radio />}
                                  label={option.value}
                                />
                              ))}
                            </RadioGroup>
                            {validationErrors.selectedAccidentFeature && (
                              <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
                                未稼働理由を選択してください
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: "none" }}>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {CarNoopEventsConstants.RestartPlanDate}
                      </Typography>
                      <FormControl component="fieldset" error={validationErrors.restartPlanDateKey}>
                        <RadioGroup
                          value={editState.restartPlanDateKey ?? ""}
                          onChange={handleChangeRestartDateType}
                          row
                          sx={{ mt: 1, mb: 1 }}
                        >
                          {restartOptions.map((option) => (
                            <FormControlLabel
                              key={option.key}
                              value={option.key}
                              control={<Radio />}
                              label={option.value}
                              disabled={showRestartUnknownOnly}
                            />
                          ))}
                        </RadioGroup>
                        {validationErrors.restartPlanDateKey && (
                          <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
                            稼働再開見込みを選択してください
                          </Typography>
                        )}
                      </FormControl>
                      {!showRestartUnknownOnly && (
                        <Typography variant="body2">
                          ※本プロジェクトでは他タクシー車両と同等程度の運行データ取得が無ければ、技術実証のため対価としての助成金の返納対象になり得ます。<br />
                          ※「2週間以上/不明」や「該当期間稼働しており、未稼働ではない」をご選択の場合、別途お電話にて状況をお伺いさせていただきます。<br />
                        </Typography>
                      )}
                      <Typography variant="body2">
                        ※事故報告済みの未稼働理由が選択された場合は、「2週間以上/不明」が自動で選択されます。
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <ActionButtons
                btnType="Register"
                loading={loading}
                onCancel={goToList}
                onSubmit={onSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <CarNoopConfirmDialog
        visible={showConfirm}
        headerTitle={CarNoopEventsConstants.Confirm}
        onCancel={() => setShowConfirm(false)}
        onOk={() => {
          setShowConfirm(false);
          upSert();
        }}
        message={getEditWarnMessage(editState, accidentReports)}
        showWarning={showConfirmWarning}
      />

      {afterSubmitDialogInfo &&
        <ConfirmDialog
          visible={true}
          headerTitle={afterSubmitDialogInfo.title}
          headerType={afterSubmitDialogInfo.isInfoDialog ? "Normal" : undefined}
          showCancelBtn={!afterSubmitDialogInfo.isInfoDialog}
          textAlign="left"
          onCancel={() => {
            handleAfterSubmitDialogClose(false);
          }}
          onOk={() => {
            handleAfterSubmitDialogClose(!afterSubmitDialogInfo.isInfoDialog);
          }}
        >
          <Typography gutterBottom sx={{ whiteSpace: "pre-line" }}>
            {afterSubmitDialogInfo.message}
          </Typography>
        </ConfirmDialog>
      }
    </React.Fragment>
  );
};
